import { SeoReport, KeywordResult, CompetitorAnalysis, ContentIdea } from "../types";
import apiRequest from "./api";

// All AI processing is now handled by the backend API
// These functions make API calls to the backend

// --- 1. EXISTING AUDIT FUNCTION (Legacy) ---
export const analyzeContent = async (url: string, htmlContent: string): Promise<SeoReport> => {
  return apiRequest<SeoReport>('/analysis/audit', {
    method: 'POST',
    body: JSON.stringify({ url, html: htmlContent }),
  });
};

// --- 1.1. PROGRESSIVE AUDIT FUNCTION (SSE via Fetch) ---
export interface AuditProgress {
  step: 'extraction' | 'enhanced' | 'ai-analysis' | 'link-performance';
  title: string;
  status: 'running' | 'completed' | 'skipped' | 'error' | 'waiting' | 'timeout' | 'failed';
  message: string;
  progress?: number; // 0-100
  estimatedTimeRemaining?: number; // milliseconds
  duration?: number; // milliseconds (when completed)
  data?: any;
}

export interface AuditProgressCallback {
  onProgress?: (progress: AuditProgress) => void;
  onComplete?: (report: SeoReport) => void;
  onError?: (error: string) => void;
}

export const analyzeContentProgressive = async (
  url: string, 
  htmlContent: string,
  callbacks: AuditProgressCallback,
  options?: {
    usePuppeteer?: boolean;
    instantMetrics?: any;
  }
): Promise<SeoReport> => {
  // No token needed - cookies sent automatically with credentials: 'include'
  // Use relative path for Vite proxy, or full URL if VITE_API_URL is set
  const API_URL = import.meta.env.VITE_API_URL 
    ? (import.meta.env.VITE_API_URL.endsWith('/api') 
        ? import.meta.env.VITE_API_URL 
        : `${import.meta.env.VITE_API_URL}/api`)
    : '/api'; // Use relative path for Vite proxy
  
  const response = await fetch(`${API_URL}/analysis/audit-stream`, {
    method: 'POST',
    credentials: 'include' as RequestCredentials, // Include cookies
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      url, 
      html: htmlContent,
      usePuppeteer: options?.usePuppeteer,
      instantMetrics: options?.instantMetrics
    })
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Request failed');
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalReport: SeoReport | null = null;
  let pendingScreenshot: string | null = null;
  let pendingMobileScreenshot: string | null = null;
  let screenshotEventReceived = false;
  let mobileScreenshotEventReceived = false;
  let completeEventReceived = false;

  if (!reader) {
    throw new Error('Stream not available');
  }

  console.log('[Frontend] 🔄 Starting SSE stream reading...');

  while (true) {
    const { done, value } = await reader.read();
    
    if (done) {
      console.log('[Frontend] 📴 SSE stream ended. Events received:', {
        screenshot: screenshotEventReceived,
        mobileScreenshot: mobileScreenshotEventReceived,
        complete: completeEventReceived,
        hasPendingScreenshot: !!pendingScreenshot,
        hasFinalReport: !!finalReport
      });
      break;
    }
    
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer
    
    for (const chunk of lines) {
      if (!chunk.trim()) continue;
      
      const lines = chunk.split('\n');
      let event = 'message';
      let data = '';
      
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          event = line.substring(7);
        } else if (line.startsWith('data: ')) {
          data = line.substring(6);
        }
      }
      
      if (!data) continue;
      
      try {
        const parsed = JSON.parse(data);
        
        if (event === 'progress') {
          const progress: AuditProgress = parsed;
          if (callbacks.onProgress) {
            callbacks.onProgress(progress);
          }
          if (progress.data && progress.step === 'ai-analysis') {
            finalReport = progress.data;
            // Attach pending screenshots if we have them
            if (pendingScreenshot && finalReport) {
              (finalReport as any).screenshot = pendingScreenshot;
              pendingScreenshot = null;
            }
            if (pendingMobileScreenshot && finalReport) {
              (finalReport as any).mobileScreenshot = pendingMobileScreenshot;
              pendingMobileScreenshot = null;
            }
          }
        } else if (event === 'ai-chunk') {
          // Handle streaming AI response
          const { chunk, accumulated } = parsed;
          
          // Try to parse partial JSON if it looks complete
          try {
            const partial = JSON.parse(accumulated);
            if (partial.overallScore !== undefined) {
              // Valid partial report
              if (callbacks.onProgress) {
                callbacks.onProgress({
                  step: 'ai-analysis',
                  title: 'AI Analysis',
                  status: 'running',
                  message: 'Streaming analysis...',
                  progress: 50,
                  data: partial
                });
              }
            }
          } catch {
            // Not valid JSON yet, keep accumulating
          }
        } else if (event === 'screenshot') {
          // [DEBUG] Step 4: Verify Frontend SSE Reception
          console.log('[DEBUG] Frontend received screenshot event:', {
            hasScreenshot: !!parsed.screenshot,
            screenshotLength: parsed.screenshot?.length || 0,
            screenshotPreview: parsed.screenshot ? parsed.screenshot.substring(0, 50) : 'none',
            hasFinalReport: !!finalReport,
            hasPendingScreenshot: !!pendingScreenshot
          });
          
          // Receive screenshot separately - store it for later attachment
          if (parsed.screenshot) {
            if (finalReport) {
              (finalReport as any).screenshot = parsed.screenshot;
              // Also attach to comprehensive if it exists
              if ((finalReport as any).comprehensive) {
                ((finalReport as any).comprehensive as any).screenshot = parsed.screenshot;
              }
              console.log('[Frontend] 📸 Screenshot received separately and attached (length:', parsed.screenshot.length, ')');
            } else {
              // Store for later attachment when report arrives
              pendingScreenshot = parsed.screenshot;
              console.log('[Frontend] 📸 Screenshot received separately, storing for later attachment (length:', parsed.screenshot.length, ')');
            }
          }
        } else if (event === 'mobile-screenshot') {
          console.log('[DEBUG] Frontend received mobile-screenshot event:', {
            hasMobileScreenshot: !!parsed.mobileScreenshot,
            mobileScreenshotLength: parsed.mobileScreenshot?.length || 0,
            hasFinalReport: !!finalReport,
            hasPendingMobileScreenshot: !!pendingMobileScreenshot
          });
          
          // Receive mobile screenshot separately - store it for later attachment
          if (parsed.mobileScreenshot) {
            if (finalReport) {
              (finalReport as any).mobileScreenshot = parsed.mobileScreenshot;
              // Also attach to comprehensive if it exists
              if ((finalReport as any).comprehensive) {
                ((finalReport as any).comprehensive as any).mobileScreenshot = parsed.mobileScreenshot;
              }
              console.log('[Frontend] 📱 Mobile screenshot received separately and attached (length:', parsed.mobileScreenshot.length, ')');
            } else {
              // Store for later attachment when report arrives
              pendingMobileScreenshot = parsed.mobileScreenshot;
              console.log('[Frontend] 📱 Mobile screenshot received separately, storing for later attachment (length:', parsed.mobileScreenshot.length, ')');
            }
          }
        } else if (event === 'complete') {
          // [DEBUG] Step 4: Verify complete event reception
          console.log('[DEBUG] Frontend received complete event:', {
            hasPendingScreenshot: !!pendingScreenshot,
            hasPendingMobileScreenshot: !!pendingMobileScreenshot,
            reportKeys: Object.keys(parsed.report),
            hasComprehensive: !!parsed.report.comprehensive,
            comprehensiveKeys: parsed.report.comprehensive ? Object.keys(parsed.report.comprehensive) : [],
            // Check if screenshots are ALREADY in the report (new approach)
            reportHasScreenshot: !!(parsed.report as any).screenshot,
            reportScreenshotLength: (parsed.report as any).screenshot?.length || 0,
            reportHasMobileScreenshot: !!(parsed.report as any).mobileScreenshot,
            comprehensiveHasScreenshot: !!parsed.report.comprehensive?.screenshot,
            comprehensiveScreenshotLength: parsed.report.comprehensive?.screenshot?.length || 0,
            // Show full structure for debugging
            reportStructure: {
              keys: Object.keys(parsed.report),
              hasScreenshot: !!(parsed.report as any).screenshot,
              hasMobileScreenshot: !!(parsed.report as any).mobileScreenshot,
              hasComprehensive: !!parsed.report.comprehensive,
              comprehensiveKeys: parsed.report.comprehensive ? Object.keys(parsed.report.comprehensive) : []
            }
          });
          
          finalReport = parsed.report;
          
          // NEW APPROACH: Screenshots should already be in the report
          // But also check for pending screenshots (backward compatibility)
          if ((finalReport as any).screenshot) {
            console.log('[DEBUG] ✅ Screenshot already in report from complete event');
          } else if (pendingScreenshot && finalReport) {
            console.log('[DEBUG] Attaching pending screenshot to report (fallback)');
            (finalReport as any).screenshot = pendingScreenshot;
            // Also attach to comprehensive if it exists
            if ((finalReport as any).comprehensive) {
              ((finalReport as any).comprehensive as any).screenshot = pendingScreenshot;
              console.log('[DEBUG] ✅ Attached pending screenshot to comprehensive');
            }
            pendingScreenshot = null;
          } else {
            console.warn('[DEBUG] ⚠️ No screenshot in report and no pending screenshot!');
          }
          
          if ((finalReport as any).mobileScreenshot) {
            console.log('[DEBUG] ✅ Mobile screenshot already in report from complete event');
          } else if (pendingMobileScreenshot && finalReport) {
            console.log('[DEBUG] Attaching pending mobile screenshot to report (fallback)');
            (finalReport as any).mobileScreenshot = pendingMobileScreenshot;
            // Also attach to comprehensive if it exists
            if ((finalReport as any).comprehensive) {
              ((finalReport as any).comprehensive as any).mobileScreenshot = pendingMobileScreenshot;
              console.log('[DEBUG] ✅ Attached pending mobile screenshot to comprehensive');
            }
            pendingMobileScreenshot = null;
          }
          
          // Debug: Log screenshot availability in received report
          console.log('[Frontend] 📥 Received complete report:', {
            hasScreenshot: !!(finalReport as any).screenshot,
            screenshotLength: (finalReport as any).screenshot ? ((finalReport as any).screenshot as string).length : 0,
            hasMobileScreenshot: !!(finalReport as any).mobileScreenshot,
            mobileScreenshotLength: (finalReport as any).mobileScreenshot ? ((finalReport as any).mobileScreenshot as string).length : 0,
            hasComprehensive: !!(finalReport as any).comprehensive,
            comprehensiveHasScreenshot: !!(finalReport as any).comprehensive?.screenshot,
            comprehensiveScreenshotLength: (finalReport as any).comprehensive?.screenshot?.length || 0,
            reportKeys: Object.keys(finalReport as any).slice(0, 20),
            // Detailed screenshot location check
            screenshotLocations: {
              'finalReport.screenshot': !!(finalReport as any).screenshot,
              'finalReport.comprehensive.screenshot': !!(finalReport as any).comprehensive?.screenshot,
              'finalReport.mobileScreenshot': !!(finalReport as any).mobileScreenshot,
              'finalReport.comprehensive.mobileScreenshot': !!(finalReport as any).comprehensive?.mobileScreenshot
            },
            // Show comprehensive structure if it exists
            comprehensiveKeys: (finalReport as any).comprehensive ? Object.keys((finalReport as any).comprehensive).slice(0, 30) : null
          });
          
          // CRITICAL: Verify screenshots are actually in the report before passing to callback
          if (!(finalReport as any).screenshot && !(finalReport as any).comprehensive?.screenshot) {
            console.error('[Frontend] ❌ CRITICAL: No screenshot found in finalReport before onComplete callback!', {
              reportKeys: Object.keys(finalReport as any),
              comprehensiveExists: !!(finalReport as any).comprehensive,
              comprehensiveKeys: (finalReport as any).comprehensive ? Object.keys((finalReport as any).comprehensive) : null,
              pendingScreenshotWas: !!pendingScreenshot,
              pendingMobileScreenshotWas: !!pendingMobileScreenshot,
              screenshotEventReceived: screenshotEventReceived,
              mobileScreenshotEventReceived: mobileScreenshotEventReceived,
              completeEventReceived: completeEventReceived,
              willTryManualAttach: !!pendingScreenshot
            });
            
            // LAST RESORT: Try to attach pending screenshots if they exist
            if (pendingScreenshot && finalReport) {
              console.log('[Frontend] 🔧 LAST RESORT: Manually attaching pending screenshot');
              (finalReport as any).screenshot = pendingScreenshot;
              if ((finalReport as any).comprehensive) {
                ((finalReport as any).comprehensive as any).screenshot = pendingScreenshot;
              }
            }
            if (pendingMobileScreenshot && finalReport) {
              console.log('[Frontend] 🔧 LAST RESORT: Manually attaching pending mobile screenshot');
              (finalReport as any).mobileScreenshot = pendingMobileScreenshot;
              if ((finalReport as any).comprehensive) {
                ((finalReport as any).comprehensive as any).mobileScreenshot = pendingMobileScreenshot;
              }
            }
          } else {
            console.log('[Frontend] ✅ Screenshot verified in finalReport before callback');
          }
          
          if (callbacks.onComplete && finalReport) {
            callbacks.onComplete(finalReport);
          }
          return finalReport;
        } else if (event === 'error') {
          const error = parsed.error || 'Unknown error';
          if (callbacks.onError) {
            callbacks.onError(error);
          }
          throw new Error(error);
        }
      } catch (e) {
        console.error('Error parsing SSE chunk:', e, chunk);
      }
    }
  }
  
  if (!finalReport) {
    throw new Error('No report received');
  }
  
  return finalReport;
};

// --- 2. KEYWORD RESEARCH TOOL (Updated) ---
export const generateKeywords = async (seed: string, country: string, language: string, intentFilter?: string): Promise<KeywordResult[]> => {
  return apiRequest<KeywordResult[]>('/analysis/keywords', {
    method: 'POST',
    body: JSON.stringify({ seed, country, language, intentFilter }),
  });
};

// --- 3. COMPETITOR ANALYSIS TOOL ---
// Note: Competitor analysis can take up to 170 seconds:
//   - 90s: Data extraction (with retries for slow websites)
//   - 20s: Parallel AI calls (keyword strategy, content strategy, advantages, weaknesses, recommendations)
//   - 60s: Final AI analysis
// Total worst case: ~170 seconds, so we set timeout to 180s (3 min) for safety
export const analyzeCompetitor = async (myUrl: string, myContent: string, compUrl: string, compContent: string): Promise<CompetitorAnalysis> => {
  return apiRequest<CompetitorAnalysis>('/analysis/competitor', {
    method: 'POST',
    body: JSON.stringify({ myUrl, myContent, compUrl, compContent }),
    timeout: 180000, // 3 minutes - allows for slow competitor websites + data extraction + parallel AI + final analysis
  });
};

// --- 4. CONTENT STRATEGY & PLANNER ---
// Generate a full SEO strategy and content calendar from the backend
export const generateSeoStrategy = async (
  topic: string,
  weeks: number
): Promise<any> => {
  return apiRequest<any>('/analysis/seo-strategy', {
    method: 'POST',
    body: JSON.stringify({ topic, weeks }),
  });
};

// Generate a full article from a strategy post/brief
export const generateArticleFromBrief = async (
  title: string,
  targetKeyword: string,
  strategyOverview: string,
  language: string
): Promise<any> => {
  return apiRequest<any>('/analysis/generate-article', {
    method: 'POST',
    body: JSON.stringify({
      title,
      target_keyword: targetKeyword,
      strategy_overview: strategyOverview,
      language,
    }),
  });
};

// Legacy placeholder (not currently used)
export const generateContentIdeas = async (
  topic: string,
  country: string,
  language: string
): Promise<ContentIdea[]> => {
  return [];
};

// --- 5. AI SEO CHAT ASSISTANT ---
export const aiChat = async (message: string, websiteUrl?: string, context?: any): Promise<{ response: string; tokens_used: number; credits_used: number }> => {
  return apiRequest<{ response: string; tokens_used: number; credits_used: number }>('/analysis/chat', {
    method: 'POST',
    body: JSON.stringify({ message, website_url: websiteUrl, context }),
  });
};

// --- 6. AUTO META TAG GENERATOR ---
export const generateMetaTags = async (pageUrl: string, pageContent: string, targetKeyword: string): Promise<any> => {
  return apiRequest<any>('/analysis/generate-meta', {
    method: 'POST',
    body: JSON.stringify({ page_url: pageUrl, page_content: pageContent, target_keyword: targetKeyword }),
  });
};

// --- 7. CONTENT OPTIMIZER & SCORER ---
export const optimizeContent = async (content: string, targetKeyword: string, competitors?: string[]): Promise<any> => {
  return apiRequest<any>('/analysis/optimize-content', {
    method: 'POST',
    body: JSON.stringify({ content, target_keyword: targetKeyword, competitors }),
  });
};

// --- 8. CONTENT BRIEF GENERATOR ---
export const generateContentBrief = async (targetKeyword: string, competitors?: string[]): Promise<any> => {
  return apiRequest<any>('/analysis/content-brief', {
    method: 'POST',
    body: JSON.stringify({ target_keyword: targetKeyword, competitors }),
  });
};

// --- 9. FAQ GENERATOR ---
export const generateFAQ = async (topic: string, targetAudience?: string): Promise<any> => {
  return apiRequest<any>('/analysis/generate-faq', {
    method: 'POST',
    body: JSON.stringify({ topic, target_audience: targetAudience }),
  });
};

// --- 10. TITLE & HEADLINE GENERATOR ---
export const generateHeadlines = async (topic: string, keyword: string, style?: 'listicle' | 'how-to' | 'guide' | 'comparison' | 'review'): Promise<any> => {
  return apiRequest<any>('/analysis/generate-headlines', {
    method: 'POST',
    body: JSON.stringify({ topic, keyword, style }),
  });
};

// DesignMind AI - Website Mockup Generator
export const generateDesignMindMockup = async (input: {
  industry: string;
  businessModel: string;
  targetAudience: string;
  goals: string;
  cms: string;
  competitors: string;
  language: 'en' | 'fr' | 'es' | 'de' | 'ar';
}): Promise<{
  mockup: any;
  creditsRemaining: number;
  usageCount: number;
  maxUsage: number;
}> => {
  return apiRequest<any>('/analysis/designmind-generate', {
    method: 'POST',
    body: JSON.stringify(input),
    timeout: 120000, // 120 seconds (2 minutes) timeout for DesignMind - complex generation needs more time
  });
};